import React from 'react';
import { Calendar, Clock, Music, ChevronRight, Info } from 'lucide-react';

const FALLBACK_IMG = '/api/uploads/logo_fallback.png';

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch { return dateStr; }
}

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return {
      day: d.toLocaleDateString('it-IT', { day: '2-digit' }),
      month: d.toLocaleDateString('it-IT', { month: 'short' }).toUpperCase(),
      year: d.getFullYear(),
    };
  } catch { return { day: '--', month: '--', year: '--' }; }
}

export default function EventsSection({ events, onEventDetail }) {
  const scrollToPrenota = (eventId) => {
    window.history.replaceState(null, '', `#prenota-${eventId}`);
    document.getElementById('prenota')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!events || events.length === 0) {
    return (
      <section id="eventi" data-testid="events-section" className="py-24" style={{ background: 'var(--bg-primary)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="section-eyebrow">Prossimi appuntamenti</p>
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-4">Prossimi Eventi</h2>
          <p className="text-gray-400 text-xl mt-8">Nuovi eventi in arrivo. Resta aggiornato!</p>
        </div>
      </section>
    );
  }

  // Feature first event, rest in grid
  const [featured, ...rest] = events;

  return (
    <section id="eventi" data-testid="events-section" className="py-24" style={{ background: 'var(--bg-primary)' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="section-eyebrow">Prossimi appuntamenti</p>
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-4">
            Prossimi <span className="text-gold italic">Eventi</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Serate indimenticabili di musica, ballo e divertimento. Ogni serata è un'esperienza unica.
          </p>
        </div>

        {/* Featured Event */}
        <div
          data-testid={`featured-event-${featured.id}`}
          className="event-card mb-8 md:flex overflow-hidden group"
          style={{ minHeight: '360px' }}
        >
          <div className="md:w-1/2 relative overflow-hidden" style={{ minHeight: '320px', background: '#080810' }}>
            <img
              src={featured.image_url || FALLBACK_IMG}
              alt={featured.title}
              className="w-full h-full absolute inset-0"
              style={featured.image_url ? { objectFit: 'contain', objectPosition: 'center', padding: '8px' } : { objectFit: 'contain', objectPosition: 'center', padding: '2.5rem', background: '#0a0a0f' }}
              onError={e => { e.target.style.objectFit='contain'; e.target.style.padding='2.5rem'; e.target.style.background='#0a0a0f'; e.target.src = FALLBACK_IMG; }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#121218]/80 hidden md:block" />
          </div>
          <div className="md:w-1/2 relative z-10 p-8 md:p-10 flex flex-col justify-center">
            <span className="inline-block bg-gold/20 text-gold text-xs font-manrope font-700 uppercase tracking-widest px-3 py-1.5 rounded-full mb-4 w-fit">
              Prossimo evento
            </span>
            <h3 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
              {featured.title}
            </h3>
            <div className="flex flex-wrap gap-4 mb-4">
              <span className="flex items-center gap-2 text-gold font-manrope text-base">
                <Calendar size={18} /> {formatDate(featured.date)}
              </span>
              <span className="flex items-center gap-2 text-aqua font-manrope text-base">
                <Clock size={18} /> Ore {featured.time}
              </span>
            </div>
            {featured.dj && (
              <span className="flex items-center gap-2 text-gray-400 font-manrope text-base mb-4">
                <Music size={18} /> {featured.dj}
              </span>
            )}
            {featured.special_guest && (
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full font-manrope font-700 text-sm"
                style={{
                  background: 'linear-gradient(90deg, rgba(201,168,76,0.15), rgba(201,168,76,0.25))',
                  border: '1px solid rgba(201,168,76,0.5)',
                  color: 'var(--color-gold)',
                  boxShadow: '0 0 20px rgba(201,168,76,0.2)',
                }}>
                <span style={{ fontSize: '1rem' }}>★</span> Special Guest: {featured.special_guest}
              </div>
            )}
            {featured.tickets_low && (
              <div className="inline-flex items-center gap-2 mb-4 ml-2 px-4 py-2 rounded-full font-manrope font-700 text-sm"
                style={{
                  background: 'linear-gradient(90deg, rgba(220,38,38,0.15), rgba(220,38,38,0.25))',
                  border: '1px solid rgba(220,38,38,0.5)',
                  color: '#f87171',
                  boxShadow: '0 0 20px rgba(220,38,38,0.2)',
                  animation: 'pulse 2s ease-in-out infinite',
                }}>
                <span style={{ fontSize: '1rem' }}>🔥</span> Biglietti in esaurimento
              </div>
            )}
            <p className="text-gray-300 text-lg mb-6 leading-relaxed">{featured.description}</p>
            <div className="flex flex-wrap gap-3">
              <button
                data-testid={`book-featured-${featured.id}`}
                onClick={() => scrollToPrenota(featured.id)}
                className="btn-primary flex items-center gap-2"
              >
                Prenota per questa serata <ChevronRight size={20} />
              </button>
              <button
                data-testid={`info-featured-${featured.id}`}
                onClick={() => onEventDetail?.(featured)}
                className="btn-secondary flex items-center gap-2 px-6"
              >
                <Info size={18} /> Info
              </button>
            </div>
          </div>
        </div>

        {/* Rest of events grid */}
        {rest.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((ev, idx) => {
              const { day, month, year } = formatDateShort(ev.date);
              return (
                <div key={ev.id} data-testid={`event-card-${ev.id}`} className="event-card group">
                  <div className="relative overflow-hidden" style={{ height: '240px' }}>
                    <img
                      src={ev.image_url || FALLBACK_IMG}
                      alt={ev.title}
                      className="w-full h-full"
                      style={ev.image_url ? { objectFit: 'cover', transition: 'transform 0.6s ease' } : { objectFit: 'contain', padding: '1.5rem', background: '#0a0a0f', transition: 'transform 0.6s ease' }}
                      onError={e => { e.target.style.objectFit='contain'; e.target.style.padding='1.5rem'; e.target.style.background='#0a0a0f'; e.target.src = FALLBACK_IMG; }}
                    />
                    {/* Date badge */}
                    <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm border border-gold/30 rounded-xl p-3 text-center z-20">
                      <p className="text-gold font-playfair font-bold text-2xl leading-none">{day}</p>
                      <p className="text-white/80 font-manrope text-xs font-600 mt-0.5">{month}</p>
                      <p className="text-white/60 font-manrope text-xs">{year}</p>
                    </div>
                  </div>

                  <div className="relative z-20 p-6">
                    <h3 className="font-playfair text-xl font-bold text-white mb-2 leading-tight">{ev.title}</h3>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex items-center gap-1.5 text-gold text-sm font-manrope">
                        <Clock size={15} /> {ev.time}
                      </span>
                      {ev.dj && (
                        <span className="flex items-center gap-1.5 text-gray-400 text-sm font-manrope">
                          <Music size={15} /> {ev.dj}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm mb-5 line-clamp-2">{ev.description}</p>
                    {ev.special_guest && (
                      <div className="inline-flex items-center gap-1.5 mb-3 px-3 py-1.5 rounded-full text-xs font-manrope font-700"
                        style={{
                          background: 'linear-gradient(90deg, rgba(201,168,76,0.12), rgba(201,168,76,0.22))',
                          border: '1px solid rgba(201,168,76,0.45)',
                          color: 'var(--color-gold)',
                          boxShadow: '0 0 14px rgba(201,168,76,0.18)',
                        }}>
                        ★ Special Guest: {ev.special_guest}
                      </div>
                    )}
                    {ev.tickets_low && (
                      <div className="inline-flex items-center gap-1.5 mb-3 ml-1 px-3 py-1.5 rounded-full text-xs font-manrope font-700"
                        style={{
                          background: 'linear-gradient(90deg, rgba(220,38,38,0.12), rgba(220,38,38,0.22))',
                          border: '1px solid rgba(220,38,38,0.45)',
                          color: '#f87171',
                          animation: 'pulse 2s ease-in-out infinite',
                        }}>
                        🔥 Biglietti in esaurimento
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        data-testid={`book-event-${ev.id}`}
                        onClick={() => scrollToPrenota(ev.id)}
                        className="btn-secondary text-sm py-3 px-4 flex-1 justify-center"
                      >
                        Prenota
                      </button>
                      <button
                        data-testid={`info-event-${ev.id}`}
                        onClick={() => onEventDetail?.(ev)}
                        className="btn-secondary text-sm py-3 px-4 justify-center"
                        style={{ flex: '0 0 auto' }}
                      >
                        <Info size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
